import { useState, useEffect, useCallback, useRef } from 'react';
import { api, GuildActivityEntry } from '@/lib/api';

interface UseGuildActivityReturn {
  activities: GuildActivityEntry[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  loadMore: () => void;
  hasMore: boolean;
}

export function useGuildActivity(guildId: string | undefined): UseGuildActivityReturn {
  const [activities, setActivities] = useState<GuildActivityEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(!!guildId);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const limit = 20;
  const fetchIdRef = useRef(0);

  const fetchActivity = useCallback(
    async (newOffset: number = 0, append: boolean = false) => {
      if (!guildId) return;
      const id = ++fetchIdRef.current;
      try {
        setLoading(true);
        const result = await api.listGuildActivity(guildId, { limit, offset: newOffset });
        if (fetchIdRef.current !== id) return;
        if (append) {
          setActivities((prev) => [...prev, ...result.activities]);
        } else {
          setActivities(result.activities);
        }
        setTotal(result.total);
        setOffset(newOffset);
      } catch (err) {
        if (fetchIdRef.current !== id) return;
        if (!append) {
          setActivities([]);
          setTotal(0);
        }
        setError(err instanceof Error ? err.message : 'Failed to load activity');
      } finally {
        if (fetchIdRef.current === id) {
          setLoading(false);
        }
      }
    },
    [guildId],
  );

  const refresh = useCallback(() => {
    setError(null);
    fetchActivity(0, false);
  }, [fetchActivity]);

  const loadMore = useCallback(() => {
    if (!loading && offset + limit < total) {
      fetchActivity(offset + limit, true);
    }
  }, [fetchActivity, loading, offset, total]);

  useEffect(() => {
    refresh();
    return () => {
      fetchIdRef.current++;
    };
  }, [refresh]);

  return {
    activities,
    total,
    loading,
    error,
    refresh,
    loadMore,
    hasMore: offset + limit < total,
  };
}
